import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Users, Plus, X, Search, Trash2, Layers, RotateCcw, ArrowLeft } from 'lucide-react';
import { api } from '../services/api';
import Avatar from '../components/Avatar';
import { useIsMobile } from '../hooks/useIsMobile';
import type { List, Contact, Agency, Role } from '../types';

interface ResolvedPayload {
  list: List;
  members: Contact[];
  member_count: number;
  email_list: string;
  resolved_at: string;
}

// ─── Bulk Builder Wizard ──────────────────────────────────────────────────────

type BulkStep = 1 | 2 | 3 | 4;

interface BulkBuilderProps {
  onClose: () => void;
  onCreated: () => void;
}

const BulkBuilder: React.FC<BulkBuilderProps> = ({ onClose, onCreated }) => {
  const qc = useQueryClient();
  const [step, setStep] = useState<BulkStep>(1);
  const [selectedAgencyIds, setSelectedAgencyIds] = useState<Set<string>>(new Set());
  const [selectedRoleIds, setSelectedRoleIds] = useState<Set<string>>(new Set());
  const [agencyFilter, setAgencyFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [candidates, setCandidates] = useState<Contact[]>([]);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [curateFilter, setCurateFilter] = useState('');
  const [listName, setListName] = useState('');
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  const { data: agencies = [] } = useQuery<Agency[]>({
    queryKey: ['agencies-bulk'],
    queryFn: () => api.getAgencies({ limit: 500 }).then((r) => r.data ?? []),
    staleTime: 5 * 60 * 1000,
  });

  const { data: roles = [] } = useQuery<Role[]>({
    queryKey: ['roles-all'],
    queryFn: () => api.getRoles(),
    staleTime: 5 * 60 * 1000,
  });

  const filteredAgencies = useMemo(() =>
    agencies.filter((a) =>
      `${a.acronym} ${a.name}`.toLowerCase().includes(agencyFilter.toLowerCase())
    ), [agencies, agencyFilter]);

  const filteredRoles = useMemo(() =>
    roles.filter((r) => r.name.toLowerCase().includes(roleFilter.toLowerCase())),
    [roles, roleFilter]);

  const toggleAgency = (id: string) => setSelectedAgencyIds((prev) => {
    const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next;
  });

  const toggleRole = (id: string) => setSelectedRoleIds((prev) => {
    const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next;
  });

  const loadCandidates = async () => {
    setLoading(true);
    try {
      const agencyIds = Array.from(selectedAgencyIds);
      const roleIds = Array.from(selectedRoleIds);
      const seen = new Set<string>();
      const results: Contact[] = [];

      if (agencyIds.length === 0 && roleIds.length === 0) {
        const res = await api.searchContacts({ active_only: true, limit: 500 });
        for (const c of res.data ?? []) {
          if (!seen.has(c.id)) { seen.add(c.id); results.push(c); }
        }
      } else if (agencyIds.length === 0) {
        for (const rid of roleIds) {
          const res = await api.searchContacts({ role_id: rid, active_only: true, limit: 500 });
          for (const c of res.data ?? []) {
            if (!seen.has(c.id)) { seen.add(c.id); results.push(c); }
          }
        }
      } else if (roleIds.length === 0) {
        for (const aid of agencyIds) {
          const res = await api.searchContacts({ agency_id: aid, active_only: true, limit: 500 });
          for (const c of res.data ?? []) {
            if (!seen.has(c.id)) { seen.add(c.id); results.push(c); }
          }
        }
      } else {
        for (const aid of agencyIds) {
          for (const rid of roleIds) {
            const res = await api.searchContacts({ agency_id: aid, role_id: rid, active_only: true, limit: 500 });
            for (const c of res.data ?? []) {
              if (!seen.has(c.id)) { seen.add(c.id); results.push(c); }
            }
          }
        }
      }

      results.sort((a, b) => {
        const na = a.full_name || `${a.last_name} ${a.first_name}`;
        const nb = b.full_name || `${b.last_name} ${b.first_name}`;
        return na.localeCompare(nb);
      });

      setCandidates(results);
      setChecked(new Set(results.map((c) => c.id)));
      setCurateFilter('');
      setStep(3);
    } finally {
      setLoading(false);
    }
  };

  const filteredCandidates = useMemo(() => {
    if (!curateFilter.trim()) return candidates;
    const q = curateFilter.toLowerCase();
    return candidates.filter((c) =>
      (c.full_name || `${c.first_name} ${c.last_name}`).toLowerCase().includes(q) ||
      (c.agency_acronym ?? c.agency_name ?? '').toLowerCase().includes(q)
    );
  }, [candidates, curateFilter]);

  const toggleContact = (id: string) => setChecked((prev) => {
    const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next;
  });

  const handleCreate = async () => {
    if (!listName.trim()) { setCreateError('List name is required.'); return; }
    setCreating(true);
    setCreateError('');
    try {
      const listRes = await api.createList({ name: listName.trim(), description: null });
      const listId = listRes.data?.id ?? listRes.id;
      const toAdd = candidates.filter((c) => checked.has(c.id));
      for (const c of toAdd) {
        await api.addListMember(listId, c.id);
      }
      qc.invalidateQueries({ queryKey: ['lists'] });
      onCreated();
    } catch (err: any) {
      setCreateError(err?.response?.data?.error ?? 'Failed to create list.');
    } finally {
      setCreating(false);
    }
  };

  const checkedCount = candidates.filter((c) => checked.has(c.id)).length;

  const stepLabel = ['Select Agencies', 'Select Roles', 'Curate Contacts', 'Name & Review'];

  return (
    <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div
        className="modal"
        style={{ maxWidth: 640, maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 16 }}>Bulk List Builder</h3>
            <div style={{ display: 'flex', gap: 4, marginTop: 6 }}>
              {([1, 2, 3, 4] as BulkStep[]).map((s) => (
                <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <div style={{
                    width: 20, height: 20, borderRadius: '50%',
                    background: step >= s ? 'var(--navy)' : 'var(--gray-200)',
                    color: step >= s ? 'white' : 'var(--gray-500)',
                    fontSize: 12, fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>{s}</div>
                  <span style={{ fontSize: 12, color: step === s ? 'var(--navy)' : 'var(--gray-500)', fontWeight: step === s ? 600 : 400 }}>
                    {stepLabel[s - 1]}
                  </span>
                  {s < 4 && <span style={{ color: 'var(--gray-300)', fontSize: 12 }}>›</span>}
                </div>
              ))}
            </div>
          </div>
          <button className="btn btn-ghost" onClick={onClose}><X size={16} /></button>
        </div>

        <div style={{ overflowY: 'auto', flex: 1 }}>

          {/* Step 1: Agencies */}
          {step === 1 && (
            <div>
              <p style={{ margin: '0 0 10px', fontSize: 14, color: 'var(--gray-600)' }}>
                Select agencies to include. Leave all unchecked to include contacts from any agency.
              </p>
              <div style={{ position: 'relative', marginBottom: 8 }}>
                <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-500)' }} />
                <input className="field-input" style={{ paddingLeft: 30 }} placeholder="Filter agencies…"
                  value={agencyFilter} onChange={(e) => setAgencyFilter(e.target.value)} />
              </div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <button className="btn btn-ghost" style={{ fontSize: 12 }}
                  onClick={() => setSelectedAgencyIds(new Set(agencies.map((a) => a.id)))}>Select All</button>
                <button className="btn btn-ghost" style={{ fontSize: 12 }}
                  onClick={() => setSelectedAgencyIds(new Set())}>Clear</button>
                {selectedAgencyIds.size > 0 && (
                  <span style={{ fontSize: 12, color: 'var(--gray-500)', alignSelf: 'center' }}>
                    {selectedAgencyIds.size} selected
                  </span>
                )}
              </div>
              <div style={{ border: '1px solid var(--gray-200)', borderRadius: 6, maxHeight: 320, overflowY: 'auto' }}>
                {filteredAgencies.map((a) => (
                  <label key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 12px',
                    borderBottom: '1px solid var(--gray-100)', cursor: 'pointer', fontSize: 14 }}>
                    <input type="checkbox" checked={selectedAgencyIds.has(a.id)}
                      onChange={() => toggleAgency(a.id)} style={{ width: 14, height: 14 }} />
                    <span style={{ fontWeight: 500 }}>{a.acronym}</span>
                    <span style={{ color: 'var(--gray-500)', fontSize: 12 }}>{a.name}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Roles */}
          {step === 2 && (
            <div>
              <p style={{ margin: '0 0 10px', fontSize: 14, color: 'var(--gray-600)' }}>
                Select roles to include. Leave all unchecked to include contacts with any (or no) role.
              </p>
              <div style={{ position: 'relative', marginBottom: 8 }}>
                <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-500)' }} />
                <input className="field-input" style={{ paddingLeft: 30 }} placeholder="Filter roles…"
                  value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} />
              </div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <button className="btn btn-ghost" style={{ fontSize: 12 }}
                  onClick={() => setSelectedRoleIds(new Set(roles.map((r) => r.id)))}>Select All</button>
                <button className="btn btn-ghost" style={{ fontSize: 12 }}
                  onClick={() => setSelectedRoleIds(new Set())}>Clear</button>
                {selectedRoleIds.size > 0 && (
                  <span style={{ fontSize: 12, color: 'var(--gray-500)', alignSelf: 'center' }}>
                    {selectedRoleIds.size} selected
                  </span>
                )}
              </div>
              <div style={{ border: '1px solid var(--gray-200)', borderRadius: 6, maxHeight: 320, overflowY: 'auto' }}>
                {filteredRoles.map((r) => (
                  <label key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 12px',
                    borderBottom: '1px solid var(--gray-100)', cursor: 'pointer', fontSize: 14 }}>
                    <input type="checkbox" checked={selectedRoleIds.has(r.id)}
                      onChange={() => toggleRole(r.id)} style={{ width: 14, height: 14 }} />
                    {r.name}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Curate */}
          {step === 3 && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <p style={{ margin: 0, fontSize: 14, color: 'var(--gray-600)' }}>
                  {candidates.length} contacts matched. Uncheck any to exclude.
                </p>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="btn btn-ghost" style={{ fontSize: 12 }}
                    onClick={() => setChecked(new Set(candidates.map((c) => c.id)))}>Select All</button>
                  <button className="btn btn-ghost" style={{ fontSize: 12 }}
                    onClick={() => setChecked(new Set())}>Deselect All</button>
                </div>
              </div>
              <div style={{ position: 'relative', marginBottom: 8 }}>
                <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-500)' }} />
                <input className="field-input" style={{ paddingLeft: 30 }} placeholder="Filter by name or agency…"
                  value={curateFilter} onChange={(e) => setCurateFilter(e.target.value)} />
              </div>
              <div style={{ border: '1px solid var(--gray-200)', borderRadius: 6, maxHeight: 320, overflowY: 'auto' }}>
                {filteredCandidates.length === 0 ? (
                  <p style={{ padding: 16, margin: 0, color: 'var(--gray-500)', fontSize: 14, textAlign: 'center' }}>
                    No contacts match
                  </p>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: 'var(--gray-50)', position: 'sticky', top: 0 }}>
                        <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600 }}></th>
                        <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600 }}>Name</th>
                        <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600 }}>Agency</th>
                        <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600 }}>Roles</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCandidates.map((c) => (
                        <tr key={c.id} style={{ borderBottom: '1px solid var(--gray-100)', opacity: checked.has(c.id) ? 1 : 0.4 }}>
                          <td style={{ padding: '5px 10px' }}>
                            <input type="checkbox" checked={checked.has(c.id)}
                              onChange={() => toggleContact(c.id)} style={{ width: 13, height: 13 }} />
                          </td>
                          <td style={{ padding: '5px 10px', fontWeight: 500 }}>
                            {c.full_name || `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim()}
                          </td>
                          <td style={{ padding: '5px 10px', color: 'var(--gray-600)' }}>
                            {c.agency_acronym ?? c.agency_name ?? '—'}
                          </td>
                          <td style={{ padding: '5px 10px', color: 'var(--gray-500)' }}>
                            {(c.roles ?? []).slice(0, 2).join(', ')}
                            {(c.roles ?? []).length > 2 && ` +${(c.roles ?? []).length - 2}`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              <p style={{ margin: '8px 0 0', fontSize: 12, color: 'var(--gray-500)', textAlign: 'right' }}>
                <strong>{checkedCount}</strong> of {candidates.length} contacts selected
              </p>
            </div>
          )}

          {/* Step 4: Name & Review */}
          {step === 4 && (
            <div>
              <p style={{ margin: '0 0 16px', fontSize: 14, color: 'var(--gray-600)' }}>
                <strong>{checkedCount}</strong> contact{checkedCount !== 1 ? 's' : ''} will be added to this list.
              </p>
              <label className="field-label">List Name *</label>
              <input
                className="field-input"
                autoFocus
                placeholder="e.g. Coastal Storm Key Liaisons"
                value={listName}
                onChange={(e) => setListName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !creating && handleCreate()}
              />
              {selectedAgencyIds.size > 0 && (
                <div style={{ marginTop: 10, fontSize: 12, color: 'var(--gray-500)' }}>
                  <strong>Agencies:</strong>{' '}
                  {agencies.filter((a) => selectedAgencyIds.has(a.id)).map((a) => a.acronym).join(', ')}
                </div>
              )}
              {selectedRoleIds.size > 0 && (
                <div style={{ marginTop: 6, fontSize: 12, color: 'var(--gray-500)' }}>
                  <strong>Roles:</strong>{' '}
                  {roles.filter((r) => selectedRoleIds.has(r.id)).map((r) => r.name).join(', ')}
                </div>
              )}
              {createError && <p style={{ color: 'var(--red)', fontSize: 12, marginTop: 10 }}>{createError}</p>}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--gray-200)' }}>
          <button
            className="btn btn-secondary"
            onClick={() => step > 1 ? setStep((step - 1) as BulkStep) : onClose()}
          >
            {step === 1 ? 'Cancel' : '← Back'}
          </button>

          {step === 1 && (
            <button className="btn btn-primary" onClick={() => setStep(2)}>
              Next: Select Roles →
            </button>
          )}
          {step === 2 && (
            <button className="btn btn-primary" disabled={loading} onClick={loadCandidates}>
              {loading ? 'Loading contacts…' : 'Next: Curate Contacts →'}
            </button>
          )}
          {step === 3 && (
            <button className="btn btn-primary" disabled={checkedCount === 0} onClick={() => setStep(4)}>
              Next: Name & Review →
            </button>
          )}
          {step === 4 && (
            <button className="btn btn-primary" disabled={creating || !listName.trim() || checkedCount === 0} onClick={handleCreate}>
              {creating ? 'Creating…' : `Create List (${checkedCount} contacts)`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const getStoredUser = () => {
  try { return JSON.parse(sessionStorage.getItem('iad2_user') || 'null'); } catch { return null; }
};

const ListsPage: React.FC = () => {
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useIsMobile();
  const currentUser = getStoredUser();
  const canManageLists = currentUser?.role === 'Administrator' || currentUser?.role === 'Superuser';
  const [selected, setSelected] = useState<List | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [archiveConfirmId, setArchiveConfirmId] = useState<string | null>(null);

  useEffect(() => {
    if (searchParams.get('create') === '1' && canManageLists) {
      setShowCreateModal(true);
      setSearchParams({}, { replace: true });
    }
  }, []); // eslint-disable-line

  const [showAddMember, setShowAddMember] = useState(false);
  const [showBulkBuilder, setShowBulkBuilder] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [newListDesc, setNewListDesc] = useState('');
  const [createError, setCreateError] = useState('');
  const [memberSearch, setMemberSearch] = useState('');
  const [addMemberSearch, setAddMemberSearch] = useState('');

  // Send List modal state
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendToInput, setSendToInput] = useState('');
  const [sendRecipients, setSendRecipients] = useState<string[]>([]);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [sendSuccess, setSendSuccess] = useState(false);

  const SEND_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const addSendRecipient = (email: string) => {
    const e = email.trim().toLowerCase();
    if (SEND_EMAIL_RE.test(e) && !sendRecipients.includes(e)) {
      setSendRecipients((prev) => [...prev, e]);
      setSendToInput('');
    }
  };
  const handleSendKeyDown = (ev: React.KeyboardEvent<HTMLInputElement>) => {
    if (ev.key === 'Enter' || ev.key === ',' || ev.key === 'Tab') {
      ev.preventDefault();
      addSendRecipient(sendToInput);
    }
    if (ev.key === 'Backspace' && sendToInput === '' && sendRecipients.length > 0) {
      setSendRecipients((prev) => prev.slice(0, -1));
    }
  };
  const handleSendBlur = () => { if (sendToInput.trim()) addSendRecipient(sendToInput); };

  const closeSendModal = () => {
    setShowSendModal(false);
    setSendToInput('');
    setSendRecipients([]);
    setSendError('');
    setSendSuccess(false);
  };

  const handleSendList = async () => {
    if (!selected || !sendRecipients.length) return;
    setSending(true);
    setSendError('');
    try {
      await api.sendList(selected.id, sendRecipients);
      setSendSuccess(true);
    } catch (err: any) {
      setSendError(err?.response?.data?.error ?? 'Failed to send list.');
    } finally {
      setSending(false);
    }
  };

  const { data: listsData, isLoading } = useQuery({
    queryKey: ['lists', showArchived ? 'archived' : 'active'],
    queryFn: () => api.getLists(showArchived ? { active_only: 'false' } : undefined),
  });

  const archiveMutation = useMutation({
    mutationFn: (id: string) => api.updateList(id, { is_active: false }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lists'] });
      setArchiveConfirmId(null);
      setSelected(null);
    },
  });

  const restoreMutation = useMutation({
    mutationFn: (id: string) => api.updateList(id, { is_active: true }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lists'] });
      setSelected(null);
    },
  });

  const lists: List[] = listsData?.data ?? [];

  const { data: resolvedData, isLoading: resolveLoading } = useQuery({
    queryKey: ['list-resolved', selected?.id],
    queryFn: () => api.resolveList(selected!.id),
    enabled: !!selected,
  });

  const resolvedPayload = resolvedData?.data as ResolvedPayload | undefined;
  const members: Contact[] = resolvedPayload?.members ?? [];
  const memberTotal: number = resolvedPayload?.member_count ?? members.length;

  const { data: searchData } = useQuery({
    queryKey: ['contacts-search', addMemberSearch],
    queryFn: () => api.searchContacts({ q: addMemberSearch, limit: 20 }),
    enabled: addMemberSearch.trim().length > 1,
  });
  const searchResults: Contact[] = searchData?.data ?? [];

  const createListMutation = useMutation({
    mutationFn: () => api.createList({ name: newListName.trim(), description: newListDesc.trim() || null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['lists'] });
      setShowCreateModal(false);
      setNewListName('');
      setNewListDesc('');
      setCreateError('');
    },
    onError: (err: any) => setCreateError(err?.response?.data?.error ?? 'Failed to create list.'),
  });

  const addMemberMutation = useMutation({
    mutationFn: (contactId: string) => api.addListMember(selected!.id, contactId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['list-resolved', selected?.id] });
      setAddMemberSearch('');
    },
  });

  // onError was missing entirely. Removing a criteria-derived member used to 404, the
  // mutation rejected, and nothing at all appeared — no toast, no refetch, unchanged row —
  // so the user clicked again and again. The backend now excludes rather than 404s, but a
  // mutation with no error path is how that stayed invisible for so long.
  const removeMemberMutation = useMutation({
    mutationFn: (contactId: string) => api.removeListMember(selected!.id, contactId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['list-resolved', selected?.id] }),
    onError: (err: any) =>
      alert(err?.response?.data?.error ?? 'Could not remove that member. Please try again.'),
  });

  const filteredMembers = memberSearch.trim()
    ? members.filter((c) => {
        const name = `${c.first_name ?? ''} ${c.last_name ?? ''}`.toLowerCase();
        return name.includes(memberSearch.toLowerCase()) ||
               (c.agency_name ?? '').toLowerCase().includes(memberSearch.toLowerCase());
      })
    : members;

  return (
    <div style={{ display: 'flex', alignItems: 'stretch', minHeight: 500, paddingBottom: 32 }}>

      {/* ── Left panel: list index ── */}
      <div style={{
        width: isMobile ? '100%' : 240,
        flexShrink: 0,
        border: '1px solid var(--gray-200)',
        borderRadius: isMobile ? 8 : '8px 0 0 8px',
        background: 'white',
        display: isMobile && selected ? 'none' : 'flex',
        flexDirection: 'column',
        zIndex: 1,
      }}>
        {/* Panel header */}
        <div style={{ padding: '12px 12px 10px', borderBottom: '1px solid var(--gray-100)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--gray-900)' }}>
              Lists
              {!isLoading && lists.length > 0 && (
                <span style={{ fontWeight: 400, color: 'var(--gray-500)', fontSize: 12, marginLeft: 6 }}>({lists.length})</span>
              )}
            </span>
            {!showArchived && canManageLists && (
              <div style={{ display: 'flex', gap: 4 }}>
                <button
                  title="Bulk Build"
                  className="btn btn-ghost"
                  style={{ padding: '2px 6px' }}
                  onClick={() => setShowBulkBuilder(true)}
                >
                  <Layers size={13} />
                </button>
                <button
                  className="btn btn-primary"
                  style={{ padding: '3px 8px', fontSize: 14 }}
                  onClick={() => { setShowCreateModal(true); setNewListName(''); setNewListDesc(''); setCreateError(''); }}
                >
                  <Plus size={13} />
                </button>
              </div>
            )}
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer', color: 'var(--gray-500)' }}>
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => { setShowArchived(e.target.checked); setSelected(null); }}
            />
            Show archived
          </label>
        </div>

        {/* List items */}
        <div style={{ flex: 1, overflowY: 'auto', maxHeight: '65vh' }}>
          {isLoading ? (
            <div style={{ padding: 16, textAlign: 'center', color: 'var(--gray-500)', fontSize: 14 }}>Loading…</div>
          ) : lists.length === 0 ? (
            <div style={{ padding: 16, textAlign: 'center', color: 'var(--gray-500)', fontSize: 14 }}>No lists</div>
          ) : (
            lists.map((l) => (
              <div
                key={l.id}
                style={{
                  padding: '9px 12px',
                  borderBottom: '1px solid var(--gray-100)',
                  cursor: 'pointer',
                  background: selected?.id === l.id ? 'var(--sky-50)' : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  opacity: showArchived ? 0.75 : 1,
                }}
                onClick={() => setSelected(l)}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 14,
                    fontWeight: selected?.id === l.id ? 600 : 400,
                    color: selected?.id === l.id ? 'var(--electric)' : 'var(--gray-900)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {l.name ?? 'Unnamed List'}
                  </div>
                  {l.type_name && (
                    <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>{l.type_name}</div>
                  )}
                </div>
                {(l.contact_count ?? 0) > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 12, color: 'var(--gray-500)', flexShrink: 0 }}>
                    <Users size={11} />
                    {(l.contact_count ?? 0).toLocaleString()}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* ── Right panel: detail ── */}
      <div style={{
        flex: 1,
        minWidth: 0,
        border: '1px solid var(--gray-200)',
        borderLeft: isMobile ? '1px solid var(--gray-200)' : 'none',
        borderRadius: isMobile ? 8 : '0 8px 8px 0',
        background: selected ? 'white' : 'var(--gray-50)',
        display: isMobile && !selected ? 'none' : undefined,
      }}>
        {!selected ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            minHeight: 300,
            gap: 10,
            color: 'var(--gray-500)',
          }}>
            <Layers size={36} strokeWidth={1.5} />
            <span style={{ fontSize: 14 }}>Select a list to view its members</span>
          </div>
        ) : (
          <div style={{ padding: '16px 20px' }}>
            {/* Detail header */}
            {isMobile && (
              <button
                className="btn btn-ghost"
                style={{ marginBottom: 12, padding: '4px 0', fontSize: 14 }}
                onClick={() => setSelected(null)}
              >
                <ArrowLeft size={14} /> Back to Lists
              </button>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>{selected.name}</h2>
              {memberTotal > 0 && (
                <span style={{ fontSize: 14, color: 'var(--gray-500)' }}>{memberTotal} member{memberTotal !== 1 ? 's' : ''}</span>
              )}
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
                {/* Send List — available to all roles when viewing active lists */}
                {!showArchived && memberTotal > 0 && (
                  <button
                    onClick={() => { setShowSendModal(true); setSendSuccess(false); setSendError(''); }}
                    style={{ background: 'var(--hazard-deep)', color: 'white', border: 'none', borderRadius: 4, padding: '5px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}
                  >
                    ✉ Send List
                  </button>
                )}
                {showArchived ? (
                  <button
                    className="btn btn-secondary"
                    style={{ fontSize: 12, padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 4 }}
                    disabled={restoreMutation.isPending}
                    onClick={() => restoreMutation.mutate(selected.id)}
                  >
                    <RotateCcw size={12} /> Restore
                  </button>
                ) : canManageLists ? (
                  <>
                    <button
                      style={{ background: 'transparent', border: '1px solid var(--gray-300)', color: 'var(--gray-500)', borderRadius: 4, padding: '3px 10px', fontSize: 12, cursor: 'pointer' }}
                      onClick={() => setArchiveConfirmId(selected.id)}
                    >
                      Archive
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={() => { setShowAddMember(true); setAddMemberSearch(''); }}
                    >
                      <Plus size={14} />
                      Add Contact
                    </button>
                  </>
                ) : null}
              </div>
            </div>

            {/* Member search */}
            {members.length > 5 && (
              <div style={{ position: 'relative', marginBottom: 12 }}>
                <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-500)' }} />
                <input
                  className="field-input"
                  style={{ paddingLeft: 32 }}
                  placeholder="Filter members…"
                  value={memberSearch}
                  onChange={(e) => setMemberSearch(e.target.value)}
                />
              </div>
            )}

            {/* Members table */}
            {resolveLoading ? (
              <div style={{ textAlign: 'center', padding: 32, color: 'var(--gray-500)' }}>Resolving list…</div>
            ) : filteredMembers.length === 0 ? (
              <div className="card" style={{ textAlign: 'center', color: 'var(--gray-500)', padding: 40 }}>
                {memberSearch ? 'No matching members' : 'No members in this list'}
              </div>
            ) : (
              <div className="card" style={{ padding: 0 }}>
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Title</th>
                      <th>Agency</th>
                      <th>Email</th>
                      <th>Type</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMembers.map((c) => {
                      const name = c.full_name || `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim() || '?';
                      return (
                        <tr key={c.id}>
                          <td style={{ fontWeight: 500 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <Avatar name={name} size={26} />
                              {name}
                            </div>
                          </td>
                          <td style={{ color: 'var(--gray-600)', fontSize: 14 }}>{c.title ?? '—'}</td>
                          <td>
                            <span className="chip">{c.agency_acronym ?? c.agency_name ?? '—'}</span>
                          </td>
                          <td style={{ fontSize: 14 }}>{c.email_primary ?? '—'}</td>
                          <td>
                            <span className={`pill ${c.classification_id === 2 ? 'pill-restricted' : 'pill-standard'}`}>
                              {c.classification_id === 2 ? 'Restricted' : 'Standard'}
                            </span>
                          </td>
                          <td>
                            <button
                              className="btn btn-ghost"
                              style={{ color: 'var(--red)', padding: '2px 6px' }}
                              title="Remove from list"
                              disabled={removeMemberMutation.isPending}
                              onClick={() => removeMemberMutation.mutate(c.id)}
                            >
                              <Trash2 size={13} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add Member Modal */}
      {showAddMember && (
        <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setShowAddMember(false); }}>
          <div className="modal" onMouseDown={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>Add Contact to List</h3>
              <button className="btn btn-ghost" onClick={() => setShowAddMember(false)}><X size={16} /></button>
            </div>
            <div style={{ position: 'relative', marginBottom: 8 }}>
              <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-500)' }} />
              <input
                className="field-input"
                style={{ paddingLeft: 32 }}
                autoFocus
                placeholder="Search contacts by name or agency…"
                value={addMemberSearch}
                onChange={(e) => setAddMemberSearch(e.target.value)}
              />
            </div>
            <div style={{ maxHeight: 300, overflowY: 'auto' }}>
              {searchResults.length === 0 && addMemberSearch.trim().length > 1 && (
                <div style={{ padding: 16, textAlign: 'center', color: 'var(--gray-500)', fontSize: 14 }}>No results</div>
              )}
              {addMemberSearch.trim().length <= 1 && (
                <div style={{ padding: 16, textAlign: 'center', color: 'var(--gray-500)', fontSize: 14 }}>Type at least 2 characters to search</div>
              )}
              {searchResults.map((c) => (
                <div
                  key={c.id}
                  style={{ padding: '8px 12px', borderBottom: '1px solid var(--gray-100)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                  onClick={() => { addMemberMutation.mutate(c.id); setShowAddMember(false); }}
                >
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>{c.full_name || `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim()}</div>
                    <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>{c.agency_name ?? ''}{c.title ? ` · ${c.title}` : ''}</div>
                  </div>
                  <Plus size={14} color="var(--blue)" />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Create List Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setShowCreateModal(false); }}>
          <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0 }}>New List</h3>
              <button className="btn btn-ghost" onClick={() => setShowCreateModal(false)}><X size={16} /></button>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label className="field-label">List Name *</label>
              <input
                className="field-input"
                type="text"
                autoFocus
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
                placeholder="e.g. Coastal Storm Key Contacts"
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label className="field-label">Description</label>
              <textarea
                className="field-input"
                rows={2}
                value={newListDesc}
                onChange={(e) => setNewListDesc(e.target.value)}
                placeholder="Optional — describe the purpose of this list"
                maxLength={1000}
              />
            </div>
            {createError && <p style={{ color: 'var(--red)', fontSize: 12, margin: '0 0 12px' }}>{createError}</p>}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>Cancel</button>
              <button
                className="btn btn-primary"
                disabled={!newListName.trim() || createListMutation.isPending}
                onClick={() => createListMutation.mutate()}
              >
                {createListMutation.isPending ? 'Creating…' : 'Create List'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showBulkBuilder && (
        <BulkBuilder
          onClose={() => setShowBulkBuilder(false)}
          onCreated={() => setShowBulkBuilder(false)}
        />
      )}

      {/* Archive Confirmation Modal */}
      {archiveConfirmId && (() => {
        const target = lists.find((l) => l.id === archiveConfirmId);
        return (
          <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setArchiveConfirmId(null); }}>
            <div className="modal" style={{ maxWidth: 440 }} onMouseDown={(e) => e.stopPropagation()}>
              <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>Archive "{target?.name}"?</h3>
              <p style={{ fontSize: 14, color: 'var(--gray-600)', marginBottom: 20 }}>
                This list will be hidden from the main lists view. Contacts in the list are not affected.
                You can restore it later using "Show archived".
              </p>
              {archiveMutation.isError && (
                <p style={{ color: 'var(--red)', fontSize: 12, marginBottom: 10 }}>Failed to archive. Try again.</p>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button className="btn btn-secondary" onClick={() => setArchiveConfirmId(null)}>Cancel</button>
                <button
                  className="btn btn-primary"
                  style={{ background: 'var(--red)', border: 'none' }}
                  disabled={archiveMutation.isPending}
                  onClick={() => archiveMutation.mutate(archiveConfirmId)}
                >
                  {archiveMutation.isPending ? 'Archiving…' : 'Archive List'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Send List modal */}
      {showSendModal && selected && (() => {
        const memberCount = memberTotal;
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: 'white', borderRadius: 10, padding: 24, width: 440, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', position: 'relative' }}>
              {sendSuccess ? (
                <>
                  <h3 style={{ margin: '0 0 10px', fontSize: 16 }}>List Sent</h3>
                  <p style={{ fontSize: 14, color: 'var(--gray-600)', marginBottom: 20 }}>
                    "{selected.name}" ({memberCount} contact{memberCount !== 1 ? 's' : ''}) was sent to{' '}
                    {sendRecipients.length === 1 ? sendRecipients[0] : `${sendRecipients.length} recipients`}.
                  </p>
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button className="btn btn-primary" onClick={closeSendModal}>Close</button>
                  </div>
                </>
              ) : (
                <>
                  <h3 style={{ margin: '0 0 4px', fontSize: 16 }}>Send "{selected.name}"</h3>
                  <p style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 16 }}>
                    {memberCount} active contact{memberCount !== 1 ? 's' : ''} will be included
                  </p>

                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray-700)', display: 'block', marginBottom: 6 }}>
                    Recipients
                  </label>
                  <div
                    style={{ display: 'flex', flexWrap: 'wrap', gap: 4, border: '1px solid var(--gray-300)', borderRadius: 6, padding: '6px 8px', minHeight: 40, cursor: 'text', background: 'white' }}
                    onClick={() => (document.getElementById('send-list-input') as HTMLInputElement | null)?.focus()}
                  >
                    {sendRecipients.map((email) => (
                      <span key={email} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'var(--hazard-deep)', color: 'white', borderRadius: 12, padding: '2px 8px', fontSize: 12, fontWeight: 600 }}>
                        {email}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setSendRecipients((prev) => prev.filter((r) => r !== email)); }}
                          style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', padding: 0, fontSize: 14, lineHeight: 1 }}
                        >×</button>
                      </span>
                    ))}
                    <input
                      id="send-list-input"
                      value={sendToInput}
                      onChange={(e) => setSendToInput(e.target.value)}
                      onKeyDown={handleSendKeyDown}
                      onBlur={handleSendBlur}
                      placeholder={sendRecipients.length === 0 ? 'Enter email, press Enter or comma to add…' : ''}
                      style={{ border: 'none', outline: 'none', fontSize: 14, minWidth: 180, flex: 1, padding: '2px 0' }}
                      autoFocus
                    />
                  </div>
                  <p style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 4, marginBottom: 16 }}>Press Enter, comma, or Tab to add each address</p>

                  {sendError && <p style={{ color: 'var(--red)', fontSize: 12, marginBottom: 12 }}>{sendError}</p>}

                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    <button className="btn btn-secondary" onClick={closeSendModal} disabled={sending}>Cancel</button>
                    <button
                      className="btn btn-primary"
                      style={{ background: 'var(--hazard-deep)', border: 'none' }}
                      disabled={sendRecipients.length === 0 || sending}
                      onClick={handleSendList}
                    >
                      {sending ? 'Sending…' : 'Send List'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default ListsPage;
